import { prefersReducedMotion } from '@theme/utilities';

const phoneLibraryPromises = new Map();
const phoneLibraryVersion = '29.1.2';

/**
 * Loads the non-module phone selector once, even when a page contains multiple
 * custom contact form sections.
 * @param {string} url
 * @returns {Promise<Function>}
 */
function loadPhoneLibrary(url) {
  if (typeof window.intlTelInput === 'function' && window.intlTelInput.version === phoneLibraryVersion) {
    return Promise.resolve(window.intlTelInput);
  }

  const pendingLoad = phoneLibraryPromises.get(url);
  if (pendingLoad) return pendingLoad;

  const libraryLoad = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.dataset.customContactPhoneLibrary = '';
    script.addEventListener(
      'load',
      () => {
        if (typeof window.intlTelInput === 'function' && window.intlTelInput.version === phoneLibraryVersion) {
          resolve(window.intlTelInput);
        } else {
          reject(new Error(`The expected phone selector version (${phoneLibraryVersion}) could not be loaded.`));
        }
      },
      { once: true }
    );
    script.addEventListener('error', () => reject(new Error('The phone selector could not be loaded.')), {
      once: true,
    });
    document.head.appendChild(script);
  });

  phoneLibraryPromises.set(url, libraryLoad);
  return libraryLoad;
}

/**
 * @param {string} url
 * @returns {Promise<Record<string, string | Function>>}
 */
async function loadPhoneTranslations(url) {
  if (!url) return {};

  try {
    const translationModule = await import(url);
    return translationModule.default ?? {};
  } catch (error) {
    console.warn('[Custom contact form] Phone selector translations could not be loaded.', error);
    return {};
  }
}

class CustomContactForm extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #fieldObserver;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {Array<Record<string, any>>} */
  #phoneFields = [];

  /** @type {Array<Record<string, any>>} */
  #validationFields = [];

  #connectionVersion = 0;
  #isPreparingSubmission = false;
  #isResubmitting = false;

  connectedCallback() {
    const connectionVersion = ++this.#connectionVersion;

    this.#cleanupPhoneFields();
    this.#cleanupValidationFields();
    this.#form = this.querySelector('form');
    this.#initializeValidationFeedback();
    this.#initializeFieldAnimations();
    void this.#initializePhoneFields(connectionVersion);
  }

  disconnectedCallback() {
    ++this.#connectionVersion;
    this.#fieldObserver?.disconnect();
    this.#cleanupPhoneFields();
    this.#cleanupValidationFields();
  }

  #initializeFieldAnimations() {
    this.#fieldObserver?.disconnect();

    if (
      this.dataset.animateFields !== 'true' ||
      prefersReducedMotion() ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }

    const fields = [...this.querySelectorAll('.custom-contact-form__field')];
    if (fields.length === 0) return;

    const fieldOrder = new Map(fields.map((field, index) => [field, index]));

    this.#fieldObserver = new IntersectionObserver(
      (entries, observer) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((first, second) => (fieldOrder.get(first.target) ?? 0) - (fieldOrder.get(second.target) ?? 0));

        visibleEntries.forEach((entry, index) => {
          if (!(entry.target instanceof HTMLElement)) return;

          entry.target.style.setProperty('--field-animation-delay', `${Math.min(index, 4) * 55}ms`);
          entry.target.classList.add('custom-contact-form__field--visible');
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -24px',
      }
    );

    fields.forEach((field) => {
      field.setAttribute('data-field-animation-ready', '');
      this.#fieldObserver?.observe(field);
    });
  }

  #initializeValidationFeedback() {
    const fields = [...this.querySelectorAll('.custom-contact-form__input')].filter(
      (field) => field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    );

    fields.forEach((field) => {
      const handleInvalid = () => {
        field.dataset.clientInvalid = 'true';
        field.setAttribute('aria-invalid', 'true');
      };
      const handleValueChange = () => {
        if (!field.validity.valid) return;

        delete field.dataset.clientInvalid;

        if (field.dataset.phoneInvalid !== 'true') {
          field.removeAttribute('aria-invalid');
        }
      };

      field.addEventListener('invalid', handleInvalid);
      field.addEventListener('input', handleValueChange);
      field.addEventListener('change', handleValueChange);
      this.#validationFields.push({ field, handleInvalid, handleValueChange });
    });
  }

  #cleanupValidationFields() {
    this.#validationFields.forEach(({ field, handleInvalid, handleValueChange }) => {
      field.removeEventListener('invalid', handleInvalid);
      field.removeEventListener('input', handleValueChange);
      field.removeEventListener('change', handleValueChange);
    });
    this.#validationFields = [];
  }

  /**
   * @param {number} connectionVersion
   */
  async #initializePhoneFields(connectionVersion) {
    const inputs = [...this.querySelectorAll('input[data-phone-picker]')].filter(
      (input) => input instanceof HTMLInputElement
    );
    const libraryUrl = this.dataset.phoneLibraryUrl;
    const utilsUrl = this.dataset.phoneUtilsUrl;

    if (inputs.length === 0 || !libraryUrl || !utilsUrl) return;

    try {
      const [intlTelInput, uiTranslations] = await Promise.all([
        loadPhoneLibrary(libraryUrl),
        loadPhoneTranslations(this.dataset.phoneLocaleUrl ?? ''),
      ]);

      if (!this.isConnected || connectionVersion !== this.#connectionVersion) return;

      const colorSchemeClass = [...this.classList].find((className) => className.startsWith('color-')) ?? '';
      const language = document.documentElement.lang || 'en';
      const supportedCountries = new Set(intlTelInput.getAllCountries().map(({ iso2 }) => iso2));

      inputs.forEach((input) => {
        const configuredCountry = input.dataset.phoneDefaultCountry?.toLowerCase() ?? '';
        const marketCountry = input.dataset.phoneMarketCountry?.toLowerCase() ?? '';
        let initialCountry = '';

        if (supportedCountries.has(configuredCountry)) {
          initialCountry = configuredCountry;
        } else if (supportedCountries.has(marketCountry)) {
          initialCountry = marketCountry;
        }

        const containerClass = ['custom-contact-form__phone-picker', colorSchemeClass].filter(Boolean).join(' ');
        const options = {
          allowedNumberTypes: null,
          containerClass,
          countryNameLocale: language,
          countrySearch: true,
          countrySelectorMode: 'AUTO',
          dropdownParent: document.body,
          formatAsYouType: true,
          initialCountry,
          loadUtils: () => import(utilsUrl),
          searchInputClass: 'custom-contact-form__phone-search',
          separateDialCode: true,
          showFlags: input.dataset.phoneShowFlags !== 'false',
          strictMode: true,
          strictRejectAnimation: true,
          uiTranslations,
          ...(initialCountry ? { countryOrder: [initialCountry] } : {}),
        };

        const instance = intlTelInput(input, options);
        const clearError = () => this.#clearPhoneError(input);
        const ready = instance.promise.then(
          () => true,
          () => false
        );

        input.addEventListener('input', clearError);
        input.addEventListener('countrychange', clearError);

        this.#phoneFields.push({
          input,
          instance,
          ready,
          clearError,
          validate: input.dataset.phoneValidate !== 'false',
        });
      });

      this.#form?.addEventListener('submit', this.#handleSubmit);
    } catch (error) {
      console.warn('[Custom contact form] The searchable phone selector could not be initialized.', error);
    }
  }

  #cleanupPhoneFields() {
    this.#form?.removeEventListener('submit', this.#handleSubmit);
    this.#form = null;
    this.#isPreparingSubmission = false;
    this.#isResubmitting = false;

    this.#phoneFields.forEach(({ input, instance, clearError }) => {
      input.removeEventListener('input', clearError);
      input.removeEventListener('countrychange', clearError);
      instance.destroy();
    });
    this.#phoneFields = [];
  }

  #handleSubmit = (event) => {
    if (this.#isResubmitting || event.defaultPrevented) return;

    event.preventDefault();
    if (this.#isPreparingSubmission) return;

    this.#isPreparingSubmission = true;
    const submitter = event.submitter;
    void this.#preparePhoneSubmission(submitter);
  };

  /**
   * @param {HTMLElement | null} submitter
   */
  async #preparePhoneSubmission(submitter) {
    const readiness = await Promise.all(this.#phoneFields.map(({ ready }) => ready));

    if (!this.isConnected || !this.#form) {
      this.#isPreparingSubmission = false;
      return;
    }

    let firstInvalidInput = null;

    this.#phoneFields.forEach((phoneField, index) => {
      const { input, instance, validate } = phoneField;
      const hasValue = input.value.trim() !== '';

      this.#clearPhoneError(input);
      if (!hasValue) return;

      if (validate && readiness[index]) {
        try {
          if (instance.isValidNumber() !== true) {
            this.#showPhoneError(input);
            firstInvalidInput ??= input;
            return;
          }
        } catch {
          // Preserve the plain field behavior if validation unexpectedly becomes unavailable.
        }
      }

      try {
        const normalizedNumber = instance.getNumber();
        if (normalizedNumber) input.value = normalizedNumber;
      } catch {
        const selectedCountry = instance.getSelectedCountry();

        if (selectedCountry?.dialCode && !input.value.trim().startsWith('+')) {
          input.value = `+${selectedCountry.dialCode} ${input.value.trim()}`;
        }
      }
    });

    this.#isPreparingSubmission = false;

    if (firstInvalidInput) {
      firstInvalidInput.focus();
      firstInvalidInput.reportValidity();
      return;
    }

    this.#isResubmitting = true;

    try {
      if (
        submitter instanceof HTMLElement &&
        this.#form.contains(submitter) &&
        'form' in submitter &&
        submitter.form === this.#form
      ) {
        this.#form.requestSubmit(submitter);
      } else {
        this.#form.requestSubmit();
      }
    } finally {
      this.#isResubmitting = false;
    }
  }

  /**
   * @param {HTMLInputElement} input
   */
  #showPhoneError(input) {
    const errorElement = this.#getPhoneError(input);
    const message = errorElement?.textContent?.trim() || 'Enter a valid phone number.';

    input.setCustomValidity(message);
    input.dataset.phoneInvalid = 'true';
    input.setAttribute('aria-invalid', 'true');

    if (errorElement) {
      input.setAttribute('aria-describedby', errorElement.id);
      errorElement.hidden = false;
    }
  }

  /**
   * @param {HTMLInputElement} input
   */
  #clearPhoneError(input) {
    const errorElement = this.#getPhoneError(input);

    input.setCustomValidity('');
    delete input.dataset.phoneInvalid;

    if (input.dataset.clientInvalid !== 'true') {
      input.removeAttribute('aria-invalid');
    }

    if (errorElement) {
      if (input.getAttribute('aria-describedby') === errorElement.id) {
        input.removeAttribute('aria-describedby');
      }
      errorElement.hidden = true;
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {HTMLElement | null}
   */
  #getPhoneError(input) {
    const errorId = input.dataset.phoneErrorId;
    return errorId ? document.getElementById(errorId) : null;
  }
}

if (!customElements.get('custom-contact-form')) {
  customElements.define('custom-contact-form', CustomContactForm);
}
